import React from "react";
import {
    Button,
    convertInputFormToObject,
    InputForm, inputStyles, SelectField,
    TextField,
    useInputForm
} from "../../../Components/Inputs";
import {clearInputFormFields} from "../../../Components/Inputs/inputUtility";
import {GridCell, gridConstants, GridContainer} from "../../../Components/Grid";
import {FORMAT_LIST, ORDER_STATUS_LIST} from "../../Library/libraryConstants";
import {SearchableSingleSelectField} from "../../../Components/Inputs/SearchableSingleSelectField";
import {createListFromArray, createListFromArrayOfObjects} from "../../../Utilities/Types/listUtilities";
import type {ListItem} from "../../../Utilities/Types/listUtilities";
import {jobTitles} from "../../../SiteConfig/jobTitles";
import {ORDER_TYPE_LIST} from "../orderConstants";
import type {OrderDistrictOption, OrderManagementSearchCriteria} from "./orderManagement.types";

interface OrderManagementSearchFormViewProps {
    buttonSharedSettings: React.ComponentProps<typeof GridCell>;
    districts: OrderDistrictOption[];
    handleSetOrderSearchCriteria: (criteria: OrderManagementSearchCriteria) => void;
    isLoading: boolean;
    orderSearchCriteria: OrderManagementSearchCriteria;
    shippingLocations: ListItem[];
    vendorList?: ListItem[];
    searchDropdowns?: Record<string, any> | null;
}

export const OrderManagementSearchFormView = ({
                                 buttonSharedSettings,
                                 handleSetOrderSearchCriteria,
                                 isLoading,
                                 orderSearchCriteria,
                                 shippingLocations,
                                 vendorList,
                                searchDropdowns,
                             }: OrderManagementSearchFormViewProps) => {

    const searchForm = useInputForm(orderSearchCriteria);

    const organizations = createListFromArray(searchDropdowns?.organizations ?? []);
    const districtOptions = createListFromArrayOfObjects(searchDropdowns?.districts, "districtId", "districtName");

    const handleClearClick = () => {
        clearInputFormFields(searchForm);
        const updatedOrderSearchCriteria = {...orderSearchCriteria};
        if (updatedOrderSearchCriteria.ClearOrderSearch)
            updatedOrderSearchCriteria.ClearOrderSearch();
        else
            updatedOrderSearchCriteria.ClearSearch(false);
    };

    const handleSearchClick = () => {
        const criteria = convertInputFormToObject(searchForm) as Record<string, unknown> & { accessNumber?: string };

        //search by catalog item access number which is the first part of the full access number
        const catalogItemAccessNumber = criteria.accessNumber?.split(" ")[0] || criteria.accessNumber;
        criteria.accessNumber = catalogItemAccessNumber;
        orderSearchCriteria.accessNumber = catalogItemAccessNumber;

        const updatedOrderSearchCriteria = {...orderSearchCriteria};
        updatedOrderSearchCriteria.Search(criteria);
        handleSetOrderSearchCriteria(updatedOrderSearchCriteria);
    };

    const inputSharedSettings = {
        large_columns: gridConstants.column.THREE,
        medium_columns: gridConstants.column.SIX,
    };

    return <>
        <InputForm name={"orderSearch"}>

            <SelectField
                {...searchForm.orderType}
                {...inputSharedSettings}
                includeDefaultOption={false}
                options={ORDER_TYPE_LIST}
            />

            <TextField
                {...searchForm.itemName}
                {...inputSharedSettings}
            />

            <SearchableSingleSelectField
                {...searchForm.shippingLocationId}
                {...inputSharedSettings}
                includeDefaultOption
                label={'Active Location List'}
                options={shippingLocations}
            />

            <TextField
                {...searchForm.studentName}
                {...inputSharedSettings}
            />

            <TextField
                {...searchForm.isbn}
                {...inputSharedSettings}
                label="ISBN"
            />

            <SelectField
                {...searchForm.format}
                {...inputSharedSettings}
                defaultOptionValue={""}
                defaultOption={"All"}
                includeDefaultOption
                options={FORMAT_LIST}
            />

            <SearchableSingleSelectField
                {...searchForm.districtId}
                {...inputSharedSettings}
                defaultOption={"All"}
                includeDefaultOption
                label={'District'}
                options={districtOptions}
            />

            <SelectField
                {...searchForm.patronRole}
                {...inputSharedSettings}
                defaultOptionValue={""}
                defaultOption={"Select a Job Title"}
                includeDefaultOption
                label={'Job Title'}
                options={createListFromArrayOfObjects(jobTitles, "jobID", "jobTitle")}
            />

            <SelectField
                {...searchForm.orderStatus}
                {...inputSharedSettings}
                defaultOptionValue={""}
                defaultOption={"Select an Order Status"}
                includeDefaultOption
                label={'Order Status'}
                options={ORDER_STATUS_LIST}
            />

            <TextField
                {...searchForm.poNumber}
                {...inputSharedSettings}
                label="PO Number"
            />
            <TextField
                {...searchForm.shelfLocation}
                {...inputSharedSettings}
            />

            <SelectField
                {...searchForm.vendorId}
                {...inputSharedSettings}
                defaultOption={"Select a Vendor"}
                defaultOptionValue={""}
                options={vendorList}
                label="Vendor"
            />
             <TextField
                {...searchForm.accessNumber}
                {...inputSharedSettings}
            />

            <SearchableSingleSelectField
                {...searchForm.organization}
                {...inputSharedSettings}
                includeDefaultOption
                label={'Organization'}
                options={organizations}
            />

            <GridCell>
                <GridContainer isReverseOrder isFlush hasPaddingX>

                    <GridCell {...buttonSharedSettings}>
                        <Button
                            buttonType={inputStyles.button.types.SEARCH}
                            disabled={isLoading}
                            label="Search"
                            name="btnSearchOrder"
                            onClick={handleSearchClick}
                            isPrimary
                        />
                    </GridCell>

                    <GridCell {...buttonSharedSettings} medium_offset={gridConstants.column.SIX}>
                        <Button
                            buttonType={inputStyles.button.types.CLEAR}
                            disabled={isLoading}
                            name="btnClearOrderSearch"
                            onClick={handleClearClick}
                            label="Clear"
                        />
                    </GridCell>

                </GridContainer>
            </GridCell>

        </InputForm>
    </>;
    };